// @ts-nocheck
import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import {
  Button, Form, Input, InputNumber, Select,
  DatePicker, Drawer, Switch, Modal, message,
} from "../components/admin-ui";
import {
  PlusOutlined, DeleteOutlined, CloseOutlined,
  CalendarOutlined, SearchOutlined, EyeOutlined,
  EditOutlined, ShoppingOutlined, CarOutlined,
  TagsOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from "../components/admin-icons";
import type { Bundle, MenuItem } from "@kawkaw/shared";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AdminShell } from "../layouts/AdminShell";
import {
  useAdminBundles, useAdminAllMenuItems, useAdminStalls,
  useCreateBundle, useUpdateBundle, useDeleteBundle,
} from "../api/hooks";
import { tokens } from "../styles/tokens";
import { formatDateOnly, formatMonthDay } from "../utils/formatLocaleDate";
const { color: T } = tokens;

/* ─── Types ──────────────────────────────────────────────── */
type LocalBundleItem = {
  menu_item_id: string;
  name: string;
  stall_name: string;
  price: number;
  image_url: string | null;
  qty: number;
  split_cost: number; // per unit the item's stall receives; defaults to price
};

/* ─── Stat card ──────────────────────────────────────────── */
function MetricCard({
  label, value, icon, iconColor, badge, badgeColor, badgeBg,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-bundle-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-bundle-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

/* ─── Applicable tabs ────────────────────────────────────── */
function ApplicableTabs({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const APPLICABLE_TABS = [
    { value: "all",      label: t("bundles.tabAll"),      icon: "≡" },
    { value: "pickup",   label: t("bundles.tabPickup"),   icon: "🛒" },
    { value: "delivery", label: t("bundles.tabDelivery"), icon: "🚚" },
  ];
  return (
    <div className="admin-bundle-tabs" style={{ display: "flex", gap: 4, background: T.surfaceSection, borderRadius: 10, padding: 4 }}>
      {APPLICABLE_TABS.map(tab => (
        <button key={tab.value} type="button" onClick={() => onChange(tab.value)} style={{
          flex: 1, padding: "8px 4px", borderRadius: 8, border: "none", cursor: "pointer",
          background: value === tab.value ? T.brand : "transparent",
          color: value === tab.value ? T.surface : T.textBody,
          fontSize: 13, fontWeight: 700, transition: "all .15s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <span>{tab.icon}</span><span>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

/* ─── Section label ──────────────────────────────────────── */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="admin-bundle-section-label" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, marginTop: 24 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand, flexShrink: 0, display: "inline-block" }} />
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.brand, textTransform: "uppercase" as const }}>{children}</span>
    </div>
  );
}

const FL = (text: string) => (
  <span className="admin-bundle-field-label" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: T.textMuted, textTransform: "uppercase" as const }}>{text}</span>
);

/* ─── Bundle card ────────────────────────────────────────── */
function BundleCard({
  bundle, itemsMap, stallsMap, onEdit, onDelete,
}: {
  bundle: Bundle;
  itemsMap: Map<string, MenuItem>;
  stallsMap: Map<string, string>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const originalPrice = parseFloat(String(bundle.original_price));
  const bundlePrice   = parseFloat(String(bundle.bundle_price));
  const savings = Math.max(0, originalPrice - bundlePrice);
  const pct     = originalPrice > 0 ? Math.round((savings / originalPrice) * 100) : 0;
  const visibleItems = bundle.items.slice(0, 3);
  const extraCount   = bundle.items.length - 3;
  const app = bundle.applicable_for;

  const validFrom = bundle.valid_from ? dayjs(bundle.valid_from) : null;
  const validTo   = bundle.valid_to   ? dayjs(bundle.valid_to)   : null;
  const dateDisplay = validFrom && validTo
    ? `${formatMonthDay(validFrom, i18n.language)} – ${formatDateOnly(validTo, i18n.language)}`
    : t("common.noExpiry");

  const confirmDelete = () => {
    Modal.confirm({
      title:   t("bundles.deleteTitle"),
      content: t("bundles.deleteContent"),
      okText:  t("bundles.deleteBtn"),
      cancelText: t("common.cancel"),
      okButtonProps: { danger: true },
      onOk: onDelete,
    });
  };

  return (
    <article className="admin-bundle-card" style={{
      background: T.surface, borderRadius: 16, border: "1px solid #F3F4F6",
      padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      {/* Header */}
      <div className="admin-bundle-card-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h2 className="admin-bundle-card-title" style={{ fontSize: 15, fontWeight: 800, color: T.textDark, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bundle.name}</h2>
        <span className="admin-bundle-status" style={{
          fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 100, flexShrink: 0,
          background: bundle.is_active ? T.successBg : T.surfaceSection,
          color:      bundle.is_active ? T.successText : T.textSecondary,
        }}>
          {bundle.is_active ? t("bundles.pillActive") : t("bundles.pillDisabled")}
        </span>
      </div>

      {/* Item thumbnails */}
      <div className="admin-bundle-items" style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {visibleItems.map((item, i) => {
          const mi = itemsMap.get(item.menu_item_id);
          const stallName = mi ? (stallsMap.get(mi.stall_id) ?? "") : "";
          return (
            <div className="admin-bundle-item" key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: 68 }}>
              <div className="admin-bundle-item-image" style={{ width: 56, height: 56, borderRadius: 12, background: T.bg, overflow: "hidden", border: "1px solid #F3F4F6", flexShrink: 0 }}>
                {mi?.image_url ? (
                  <img src={mi.image_url} alt={mi.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: T.borderLight }}>🍽</div>
                )}
              </div>
              <div style={{ fontSize: 10, color: T.textDark, fontWeight: 600, textAlign: "center", width: 68, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {mi?.name ?? "—"}
              </div>
              <div style={{ fontSize: 10, color: T.textMuted, textAlign: "center", width: 68, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {stallName}
              </div>
            </div>
          );
        })}
        {extraCount > 0 && (
          <div style={{
            width: 56, height: 56, borderRadius: 12, background: T.surfaceSection,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: T.textMuted,
          }}>
            +{extraCount}
          </div>
        )}
      </div>

      {/* Price + applicable */}
      <div className="admin-bundle-pricing" style={{ marginBottom: 12 }}>
        <div className="admin-bundle-price-row" style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <span className="admin-bundle-original-price" style={{ fontSize: 13, color: T.textMuted, textDecoration: "line-through" }}>RM {originalPrice.toFixed(2)}</span>
          <span className="admin-bundle-price" style={{ fontSize: 22, fontWeight: 900, color: T.brand }}>RM {bundlePrice.toFixed(2)}</span>
        </div>
        <div className="admin-bundle-price-meta" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {savings > 0 ? (
            <span className="admin-bundle-saving" style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: T.successBg, color: T.successText }}>
              {t("common.savePct", { amount: savings.toFixed(2), pct })}
            </span>
          ) : <span />}
          <span className="admin-bundle-applicable" style={{ fontSize: 12, color: T.textBody, display: "flex", alignItems: "center", gap: 4 }}>
            {app === "pickup"   ? <><ShoppingOutlined /> {t("common.pickupOnly")}</>
             : app === "delivery" ? <><CarOutlined /> {t("common.deliveryOnly")}</>
             : <><ShoppingOutlined /><CarOutlined style={{ marginLeft: 2 }} /> {t("common.pickupAndDelivery")}</>}
          </span>
        </div>
      </div>

      {/* Divider + footer */}
      <div style={{ height: 1, background: T.surfaceSection, marginBottom: 10 }} />
      <footer className="admin-bundle-card-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="admin-bundle-validity" style={{ fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 5 }}>
          <CalendarOutlined /> {dateDisplay}
        </span>
        <div className="admin-bundle-actions" style={{ display: "flex", gap: 2 }}>
          <Button type="text" icon={<EditOutlined />} size="small" aria-label={t("common.edit")} onClick={onEdit} style={{ color: T.textBody }} />
          <Button type="text" icon={<EyeOutlined />} size="small" aria-label={t("bundles.previewAction")} style={{ color: T.successBright }} />
          <Button type="text" icon={<DeleteOutlined />} size="small" aria-label={t("common.delete")} onClick={confirmDelete} style={{ color: T.dangerText }} />
        </div>
      </footer>
    </article>
  );
}

/* ─── Screen ─────────────────────────────────────────────── */
export function BundlesScreen() {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState<Bundle | null>(null);
  const [form]                      = Form.useForm();
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter]       = useState("all");
  const [applicableFilter, setApplicableFilter] = useState("all");

  /* Drawer state */
  const [bundleItems,      setBundleItems]      = useState<LocalBundleItem[]>([]);
  const [bundlePrice,      setBundlePrice]      = useState<number>(0);
  const [applicableTo,     setApplicableTo]     = useState("all");
  const [hasExpiry,        setHasExpiry]        = useState(false);
  const [isActive,         setIsActive]         = useState(true);
  const [showItemSelector, setShowItemSelector] = useState(false);

  const { data: bundlesData, isLoading } = useAdminBundles();
  const { data: allItemsData }           = useAdminAllMenuItems();
  const { data: stallsData }             = useAdminStalls();
  const createBundle = useCreateBundle();
  const updateBundle = useUpdateBundle();
  const deleteBundle = useDeleteBundle();

  const bundles  = (bundlesData?.data ?? []) as Bundle[];
  const allItems = (allItemsData?.data ?? []) as MenuItem[];

  const itemsMap = useMemo(() => new Map(allItems.map(i => [i.id, i])), [allItems]);
  const stallsMap = useMemo(() =>
    new Map((stallsData?.data ?? []).map((s: { id: string; name: string }) => [s.id, s.name])),
    [stallsData]);

  /* Stats */
  const totalBundles  = bundles.length;
  const activeCount   = bundles.filter(b => b.is_active).length;
  const disabledCount = bundles.filter(b => !b.is_active).length;

  /* Filtered */
  const filtered = useMemo(() => {
    let rows = bundles;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(b => b.name.toLowerCase().includes(q));
    }
    if (statusFilter === "active")   rows = rows.filter(b =>  b.is_active);
    if (statusFilter === "disabled") rows = rows.filter(b => !b.is_active);
    if (applicableFilter !== "all")  rows = rows.filter(b => b.applicable_for === applicableFilter);
    return rows;
  }, [bundles, search, statusFilter, applicableFilter]);

  /* Derived */
  const originalTotal = useMemo(() =>
    bundleItems.reduce((s, i) => s + i.price * i.qty, 0), [bundleItems]);
  const splitTotal  = useMemo(() => bundleItems.reduce((s, i) => s + i.split_cost * i.qty, 0), [bundleItems]);
  const savings     = Math.max(0, originalTotal - bundlePrice);
  const savingsPct  = originalTotal > 0 ? Math.round((savings / originalTotal) * 100) : 0;

  /* Item options for the selector */
  const itemOptions = useMemo(() =>
    allItems.map(mi => ({
      value: mi.id,
      label: `${mi.name} — ${stallsMap.get(mi.stall_id) ?? t("bundles.unknownStall")}`,
    })), [allItems, stallsMap, t]);

  /* Handlers */
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setBundleItems([]);
    setBundlePrice(0);
    setApplicableTo("all");
    setHasExpiry(false);
    setIsActive(true);
    setShowItemSelector(false);
    setDrawerOpen(true);
  };

  const openEdit = (b: Bundle) => {
    setEditing(b);
    form.setFieldsValue({
      name:        b.name,
      description: b.description,
      valid_from:  b.valid_from ? dayjs(b.valid_from) : null,
      valid_until: b.valid_to   ? dayjs(b.valid_to)   : null,
    });
    const localItems: LocalBundleItem[] = b.items.map(item => {
      const mi = itemsMap.get(item.menu_item_id);
      return {
        menu_item_id: item.menu_item_id,
        name:         mi?.name ?? item.menu_item_id,
        stall_name:   mi ? (stallsMap.get(mi.stall_id) ?? "") : "",
        price:        mi ? parseFloat(String(mi.price)) : 0,
        image_url:    mi?.image_url ?? null,
        qty:          item.qty,
        split_cost:   item.split_cost != null ? Number(item.split_cost) : (mi ? parseFloat(String(mi.price)) : 0),
      };
    });
    setBundleItems(localItems);
    setBundlePrice(parseFloat(String(b.bundle_price)));
    setApplicableTo(b.applicable_for === "both" ? "all" : b.applicable_for);
    setHasExpiry(!!(b.valid_from && b.valid_to));
    setIsActive(b.is_active);
    setShowItemSelector(false);
    setDrawerOpen(true);
  };

  const onSave = async (values: Record<string, unknown>) => {
    const itemsPayload = bundleItems.map(i => ({ menu_item_id: i.menu_item_id, qty: i.qty, split_cost: i.split_cost }));
    const payload = {
      name:           values.name as string,
      description:    (values.description as string | undefined) ?? null,
      items:          itemsPayload,
      category_ids:   [],
      original_price: originalTotal,
      bundle_price:   bundlePrice,
      applicable_for: applicableTo === "all" ? "both" : applicableTo,
      is_active:      isActive,
      valid_from:  hasExpiry && values.valid_from  ? (values.valid_from  as dayjs.Dayjs).toISOString() : dayjs().toISOString(),
      valid_to:    hasExpiry && values.valid_until ? (values.valid_until as dayjs.Dayjs).toISOString() : dayjs().add(5, "year").toISOString(),
    };
    try {
      if (editing) {
        await updateBundle.mutateAsync({ id: editing.id, ...payload } as unknown as Partial<Bundle> & { id: string });
        void message.success(t("bundles.updateSuccess"));
      } else {
        await createBundle.mutateAsync(payload as unknown as Partial<Bundle>);
        void message.success(t("bundles.createSuccess"));
      }
      setDrawerOpen(false);
    } catch {
      void message.error(t("bundles.saveFailed"));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteBundle.mutateAsync(id);
      void message.success(t("bundles.deleteSuccess"));
    } catch {
      void message.error(t("bundles.deleteFailed"));
    }
  };

  const addItem = (menuItemId: string) => {
    if (!menuItemId) return;
    const mi = itemsMap.get(menuItemId);
    if (!mi) return;
    setBundleItems(prev => {
      const existing = prev.find(i => i.menu_item_id === menuItemId);
      if (existing) return prev.map(i => i.menu_item_id === menuItemId ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, {
        menu_item_id: menuItemId,
        name:         mi.name,
        stall_name:   stallsMap.get(mi.stall_id) ?? "",
        price:        parseFloat(String(mi.price)),
        image_url:    mi.image_url,
        qty:          1,
        split_cost:   parseFloat(String(mi.price)),
      }];
    });
    setShowItemSelector(false);
  };

  const removeItem = (menuItemId: string) => {
    setBundleItems(prev => prev.filter(i => i.menu_item_id !== menuItemId));
  };

  return (
    <AdminShell>
      <div className="admin-bundles-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
      {/* ── Page header ── */}
      <div className="admin-bundles-toolbar">
        <div>
          <h1 className="admin-page-title">{t("bundles.title")}</h1>
          <p className="admin-bundles-subtitle">{t("bundles.pageSubtitle")}</p>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />} onClick={openCreate}
          className="admin-bundles-create"
        >
          {t("bundles.createBundle")}
        </Button>
      </div>

      {/* ── Stat cards ── */}
      <div className="admin-bundles-stats grid">
        <MetricCard
          label={t("bundles.statTotal")} value={totalBundles}
          icon={<TagsOutlined />} iconColor={T.brand}
          badge={t("bundles.statBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
        />
        <MetricCard
          label={t("bundles.statActive")} value={activeCount}
          icon={<CheckCircleOutlined />} iconColor={T.successBright}
          badge={t("bundles.statBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg}
        />
        <MetricCard
          label={t("bundles.statDisabled")} value={disabledCount}
          icon={<CloseCircleOutlined />} iconColor={T.dangerText}
          badge={t("bundles.statBadgeDisabled")} badgeColor={T.dangerText} badgeBg={T.dangerBg}
        />
      </div>

      {/* ── Filter bar ── */}
      <div className="admin-bundles-filters admin-txn-filter-panel">
        <Input
          prefix={<SearchOutlined style={{ color: T.textMuted }} />}
          placeholder={t("bundles.searchPlaceholder")}
          value={search} onChange={e => setSearch(e.target.value)} allowClear
          style={{ height: 40, borderRadius: 10 }}
        />
        <Select
          value={statusFilter} onChange={setStatusFilter} style={{ height: 40 }}
          options={[
            { value: "all",      label: t("bundles.statusAll") },
            { value: "active",   label: t("bundles.statusActiveOnly") },
            { value: "disabled", label: t("bundles.statusDisabledOnly") },
          ]}
        />
        <Select
          value={applicableFilter} onChange={setApplicableFilter} style={{ height: 40 }}
          options={[
            { value: "all",      label: t("bundles.applicableAll") },
            { value: "both",     label: t("bundles.applicableBoth") },
            { value: "pickup",   label: t("bundles.applicablePickup") },
            { value: "delivery", label: t("bundles.applicableDelivery") },
          ]}
        />
      </div>

      {/* ── Card grid ── */}
      <p className="admin-bundles-results">{t("common.total", { count: filtered.length })}</p>
      {isLoading ? (
        <div className="admin-bundles-state">{t("bundles.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="admin-bundles-state">{t("bundles.empty")}</div>
      ) : (
        <div className="admin-bundle-grid grid">
          {filtered.map(bundle => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              itemsMap={itemsMap}
              stallsMap={stallsMap}
              onEdit={() => openEdit(bundle)}
              onDelete={() => void onDelete(bundle.id)}
            />
          ))}
        </div>
      )}
        </div>
      </div>

      {/* ── Create / Edit Drawer ── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={460}
        closeIcon={null}
        title={
          <div className="admin-bundles-drawer-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="admin-bundles-drawer-title" style={{ fontSize: 20, fontWeight: 800, color: T.textDark, lineHeight: 1.2 }}>
                {editing ? t("bundles.editTitle") : t("bundles.newTitle")}
              </div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4, fontWeight: 400 }}>
                {editing ? editing.name : t("bundles.drawerSubtitleCreate")}
              </div>
            </div>
            <Button className="admin-duplicate-close" type="text" icon={<CloseOutlined />} onClick={() => setDrawerOpen(false)}
              style={{ color: T.textBody, marginTop: -4, marginRight: -8 }} />
          </div>
        }
        footer={
          <div style={{ display: "flex", gap: 12, padding: "4px 0" }}>
            <Button
              style={{ flex: 1, height: 48, borderRadius: 10, fontWeight: 700, fontSize: 14 }}
              onClick={() => setDrawerOpen(false)}
            >
              {t("bundles.cancel").toUpperCase()}
            </Button>
            <Button
              type="primary"
              style={{ flex: 2, height: 48, borderRadius: 10, fontWeight: 700, fontSize: 14, background: T.brand, borderColor: T.brand }}
              loading={createBundle.isPending || updateBundle.isPending}
              onClick={() => void form.submit()}
            >
              {editing ? t("bundles.saveChanges").toUpperCase() : t("bundles.saveBundle").toUpperCase()}
            </Button>
          </div>
        }
        styles={{ footer: { padding: "16px 24px", borderTop: "1px solid #F3F4F6" } }}
        maskClosable={false}
        destroyOnClose
      >
        <div className="admin-bundles-form">
        <Form form={form} layout="vertical" onFinish={onSave} colon={false}>

          {/* ── BASIC INFORMATION ── */}
          <SectionLabel>{t("bundles.sectionBasic")}</SectionLabel>

          <Form.Item name="name" label={FL(t("bundles.bundleName"))} rules={[{ required: true, message: t("bundles.nameRequired") }]} style={{ marginBottom: 14 }}>
            <Input placeholder={t("bundles.namePlaceholder")} style={{ height: 44, borderRadius: 10, background: T.bg }} />
          </Form.Item>

          <Form.Item name="description" label={FL(t("bundles.description"))} style={{ marginBottom: 8 }}>
            <Input.TextArea rows={3} style={{ borderRadius: 10, background: T.bg, resize: "none" }} />
          </Form.Item>

          {/* ── ITEM IN THIS BUNDLE ── */}
          <div className="admin-bundle-items-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand, display: "inline-block" }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.brand, textTransform: "uppercase" }}>{t("bundles.sectionItems")}</span>
            </div>
            <Button
              className="admin-bundle-add-item"
              type="text" size="small"
              onClick={() => setShowItemSelector(v => !v)}
              style={{ color: T.successBright, fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>⊕</span> {t("bundles.addItem")}
            </Button>
          </div>

          {showItemSelector && (
            <div style={{ marginBottom: 12 }}>
              <Select
                placeholder={t("bundles.searchItemPlaceholder")}
                style={{ width: "100%" }}
                options={itemOptions}
                onChange={(value: unknown) => { if (value) addItem(String(value)); }}
              />
            </div>
          )}

          {/* Items list */}
          <div className="admin-bundle-form-items" style={{
            background: T.bg, borderRadius: 12,
            border: "1px solid #F3F4F6", marginBottom: 8,
            padding: bundleItems.length === 0 ? "14px 16px" : 0,
          }}>
            {bundleItems.length === 0 ? (
              <div style={{ textAlign: "center", color: T.textMuted, fontSize: 13 }}>{t("bundles.noItemsYet")}</div>
            ) : (
              <>
                {bundleItems.map((item, i) => (
                  <div className="admin-bundle-form-item" key={item.menu_item_id} style={{
                    display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
                    padding: "12px 16px",
                    borderBottom: i < bundleItems.length - 1 ? "1px solid #F3F4F6" : "none",
                  }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: T.surfaceSection, overflow: "hidden", flexShrink: 0 }}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🍽</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{item.name}</div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>{item.stall_name}</div>
                    </div>
                    <span style={{ fontWeight: 700, color: T.textDark, marginRight: 4, flexShrink: 0 }}>RM {item.price.toFixed(2)}</span>
                    <Button
                      className="admin-bundle-remove-item"
                      type="text" size="small" icon={<CloseOutlined style={{ fontSize: 12 }} />}
                      aria-label={`${t("common.delete")} ${item.name}`}
                      onClick={() => removeItem(item.menu_item_id)}
                      style={{ color: T.textMuted, flexShrink: 0 }}
                    />
                    <div className="admin-bundle-split">
                      <span style={{ fontSize: 12, color: T.textMuted }}>{t("bundles.splitCost")}</span>
                      <InputNumber
                        value={item.split_cost}
                        min={0} max={item.price} precision={2} controls={false}
                        prefix="RM"
                        onChange={v => setBundleItems(prev => prev.map(x => x.menu_item_id === item.menu_item_id ? { ...x, split_cost: v ?? 0 } : x))}
                        style={{ width: 130 }}
                      />
                    </div>
                  </div>
                ))}
                <div style={{ textAlign: "right", padding: "10px 16px", borderTop: "1px solid #F3F4F6", fontSize: 13, color: T.textBody }}>
                  {t("bundles.originalTotal")} <b style={{ color: T.textDark }}>RM {originalTotal.toFixed(2)}</b>
                  <div style={{ marginTop: 4 }}>
                    {t("bundles.splitTotal")} <b style={{ color: T.textDark }}>RM {splitTotal.toFixed(2)}</b>
                    {bundlePrice > 0 && splitTotal <= bundlePrice && (
                      <span style={{ marginLeft: 8, color: T.textMuted }}>{t("bundles.platformKeeps", { amount: (bundlePrice - splitTotal).toFixed(2) })}</span>
                    )}
                  </div>
                  {bundlePrice > 0 && splitTotal > bundlePrice + 1e-9 && (
                    <div style={{ marginTop: 4, color: "#DC2626", fontWeight: 700 }}>
                      {t("bundles.splitOverWarning", { amount: (splitTotal - bundlePrice).toFixed(2) })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── BUNDLE PRICE ── */}
          <SectionLabel>{t("bundles.sectionBundlePrice")}</SectionLabel>

          <div className="admin-bundle-price-editor" style={{
            background: T.brandLight, border: "1px solid #FED7AA",
            borderRadius: 14, padding: "16px 18px", marginBottom: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.brand, marginBottom: 12 }}>{t("bundles.bundlePriceRm").toUpperCase()}</div>
            <div className="admin-bundle-price-input" style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 16 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: T.brand }}>RM</span>
              <InputNumber
                value={bundlePrice}
                onChange={v => setBundlePrice(v ?? 0)}
                min={0} precision={2}
                controls={false}
                variant="borderless"
                style={{ fontSize: 32, fontWeight: 900, color: T.brand, width: 160, padding: 0 }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: T.textMuted }}>{t("bundles.originalPrice")}</span>
              <span style={{ fontSize: 13, color: T.textMuted }}>RM {originalTotal.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: savings > 0 ? 10 : 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{t("bundles.bundlePriceRow")}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.brand }}>RM {bundlePrice.toFixed(2)}</span>
            </div>
            {savings > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.successBright }}>{t("bundles.customerSaves")}</span>
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 100,
                  background: T.successBg, color: T.successText,
                }}>
                  {t("common.savePct", { amount: savings.toFixed(2), pct: savingsPct })}
                </span>
              </div>
            )}
          </div>

          {/* ── APPLICABLE TO ── */}
          <SectionLabel>{t("bundles.sectionApplicable")}</SectionLabel>
          <ApplicableTabs value={applicableTo} onChange={setApplicableTo} />

          {/* ── SET EXPIRY DATE ── */}
          <div className="admin-bundle-expiry" style={{ marginTop: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hasExpiry ? 14 : 0 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{t("bundles.setExpiry")}</div>
                {!hasExpiry && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{t("bundles.noExpiryHint")}</div>
                )}
              </div>
              <Switch checked={hasExpiry} onChange={setHasExpiry} />
            </div>
            {hasExpiry && (
              <div className="admin-bundle-date-fields" style={{ display: "flex", gap: 12 }}>
                <Form.Item name="valid_from" label={FL(t("bundles.startDate"))} style={{ flex: 1, marginBottom: 0 }}>
                  <DatePicker style={{ width: "100%", height: 44, borderRadius: 10 }} />
                </Form.Item>
                <Form.Item name="valid_until" label={FL(t("bundles.endDate"))} style={{ flex: 1, marginBottom: 0 }}>
                  <DatePicker style={{ width: "100%", height: 44, borderRadius: 10 }} />
                </Form.Item>
              </div>
            )}
          </div>

          {/* ── ACTIVE STATUS ── */}
          <div className="admin-bundle-active" style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: T.bg, border: "1px solid #E5E7EB", borderRadius: 12,
            padding: "14px 16px", marginTop: 20,
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.successBright, display: "inline-block" }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{t("bundles.activeStatus")}</span>
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{t("bundles.activeStatusHint")}</div>
            </div>
            <Switch
              checked={isActive}
              onChange={setIsActive}
              style={{ background: isActive ? T.successBright : undefined }}
            />
          </div>

        </Form>
        </div>
      </Drawer>
    </AdminShell>
  );
}
